import { memo, useMemo, type CSSProperties } from 'react'
import { useI18n } from '../i18n/I18nContext'

interface Repo {
  id: number
  name: string
  full_name: string
  description?: string
  html_url: string
  homepage?: string
  topics: string[]
  language?: string
  stargazers_count: number
  forks_count: number
  archived: boolean
  disabled: boolean
}

interface StatsDashboardProps {
  repos: Repo[]
  totalRepos: number
}

type StatCardDef = {
  labelKey: string
  value: string
  total?: string
  avg?: string
  percentage?: number
  top?: string | null
  icon: string
  color: string
}

export const StatsDashboard = memo(function StatsDashboard({
  repos,
  totalRepos
}: StatsDashboardProps) {
  const { t } = useI18n()

  const stats = useMemo(() => {
    if (repos.length === 0) {
      return {
        totalStars: 0,
        totalForks: 0,
        avgStars: 0,
        avgForks: 0,
        languages: {} as Record<string, number>,
        topLanguage: null as string | null,
        topLanguages: [] as [string, number][],
        withHomepage: 0,
        archived: 0,
        totalTopics: 0,
        activeRepos: 0,
      }
    }

    const languages: Record<string, number> = {}
    const topicsSet = new Set<string>()
    let totalStars = 0
    let totalForks = 0
    let withHomepage = 0
    let archived = 0

    repos.forEach(repo => {
      totalStars += repo.stargazers_count
      totalForks += repo.forks_count

      if (repo.homepage) withHomepage++
      if (repo.archived) archived++

      if (repo.language) {
        languages[repo.language] = (languages[repo.language] || 0) + 1
      }

      repo.topics?.forEach(topic => topicsSet.add(topic))
    })

    const topLanguage = Object.entries(languages).sort((a, b) => b[1] - a[1])[0]?.[0] || null
    const topLanguages = Object.entries(languages)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)

    return {
      totalStars,
      totalForks,
      avgStars: Math.round(totalStars / repos.length),
      avgForks: Math.round(totalForks / repos.length),
      languages,
      topLanguage,
      topLanguages,
      withHomepage,
      archived,
      totalTopics: topicsSet.size,
      activeRepos: repos.length - archived
    }
  }, [repos])

  const statCards: StatCardDef[] = useMemo(() => [
    {
      labelKey: 'stats.card.totalRepos',
      value: repos.length.toLocaleString(),
      total: totalRepos.toLocaleString(),
      icon: '📦',
      color: 'var(--accent)'
    },
    {
      labelKey: 'stats.card.totalStars',
      value: stats.totalStars.toLocaleString(),
      avg: stats.avgStars.toLocaleString(),
      icon: '⭐',
      color: '#fbbf24'
    },
    {
      labelKey: 'stats.card.totalForks',
      value: stats.totalForks.toLocaleString(),
      avg: stats.avgForks.toLocaleString(),
      icon: '🍴',
      color: '#10b981'
    },
    {
      labelKey: 'stats.card.withDeploy',
      value: stats.withHomepage.toLocaleString(),
      percentage: repos.length > 0 ? Math.round((stats.withHomepage / repos.length) * 100) : 0,
      icon: '🌐',
      color: '#3b82f6'
    },
    {
      labelKey: 'stats.card.activeRepos',
      value: stats.activeRepos.toLocaleString(),
      percentage: repos.length > 0 ? Math.round((stats.activeRepos / repos.length) * 100) : 0,
      icon: '✅',
      color: '#10b981'
    },
    {
      labelKey: 'stats.card.languages',
      value: Object.keys(stats.languages).length.toLocaleString(),
      top: stats.topLanguage,
      icon: '🧠',
      color: '#8b5cf6'
    }
  ], [repos.length, stats, totalRepos])

  return (
    <div className="stats-dashboard">
      <h3 className="stats-title">{t('stats.title')}</h3>

      <div className="stats-grid">
        {statCards.map((stat, index) => (
          <div key={index} className="stat-card" style={{ '--stat-color': stat.color } as CSSProperties}>
            <div className="stat-icon">{stat.icon}</div>
            <div className="stat-content">
              <div className="stat-value">{stat.value}</div>
              <div className="stat-label">{t(stat.labelKey)}</div>
              {stat.avg && (
                <div className="stat-extra">{t('stats.extra.avg', { n: stat.avg })}</div>
              )}
              {stat.total && (
                <div className="stat-extra">{t('stats.extra.totalLabel', { n: stat.total })}</div>
              )}
              {stat.percentage !== undefined && (
                <div className="stat-extra">{t('stats.extra.percent', { n: stat.percentage })}</div>
              )}
              {stat.top && (
                <div className="stat-extra">{t('stats.extra.top', { name: stat.top })}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {stats.topLanguages && stats.topLanguages.length > 0 && (
        <div className="stats-section">
          <h4 className="stats-section-title">{t('stats.topLanguages')}</h4>
          <div className="languages-list">
            {stats.topLanguages.map(([lang, count]) => (
              <div key={lang} className="language-item">
                <span className="language-name">{lang}</span>
                <div className="language-bar-container">
                  <div
                    className="language-bar"
                    style={{
                      width: `${(count / repos.length) * 100}%`,
                      backgroundColor: 'var(--stat-color, var(--accent))'
                    }}
                  />
                </div>
                <span className="language-count">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {stats.totalTopics > 0 && (
        <div className="stats-section">
          <div className="stat-info">
            <span className="stat-info-icon">🏷️</span>
            <span className="stat-info-text">
              {stats.totalTopics === 1
                ? t('stats.topics.one', { n: stats.totalTopics })
                : t('stats.topics.other', { n: stats.totalTopics })}
            </span>
          </div>
        </div>
      )}
    </div>
  )
})
